import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Dialog from "../../components/ui/Dialog";
import Pagination from "../../components/ui/Pagination";
import { useNotCheckedIn } from "../../api/queries/widgetQueries";
import { Avatar } from "../../components/dashboard/WidgetLists";
import { formatPlainDate } from "../../utils/dateTime";
import { properName } from "../../utils/text";

const EMPTY = [];
const TYPES = ["Internal", "Deployed", "All"];

/** Numbers for the chosen employee type (byType has the same shape; "All" = the top level). */
const countsFor = (d, type) => (type !== "All" && d?.byType?.[type]) || d || {};

const untilText = (p) =>
  p.until
    ? p.until === p.from
      ? "today only"
      : `until ${formatPlainDate(p.until, "MMM D")}`
    : "";

/** "Who's on leave today" rows: avatar, name (→ profile), leave type, until. */
export const OnLeaveList = ({ list = EMPTY, canOpenProfile }) =>
  list.length === 0 ? (
    <p className="fs-080 text-slate-500 mb-0">Nobody is on leave today.</p>
  ) : (
    <ul className="dw-list">
      {list.map((p) => {
        const name = properName(p.name);
        const row = (
          <>
            <Avatar id={p.employeeId} name={name} />
            <div className="min-w-0 flex-grow-1">
              <div className="dw-name text-truncate">{name}</div>
              <div className="dw-meta text-truncate">
                {[p.leaveType, p.branchName].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="dw-meta text-nowrap">{untilText(p)}</div>
          </>
        );
        return (
          <li key={`${p.employeeId}-${p.from}`}>
            {canOpenProfile ? (
              <Link to={`/employee/${p.employeeId}`} className="dw-row dw-link">
                {row}
              </Link>
            ) : (
              <div className="dw-row">{row}</div>
            )}
          </li>
        );
      })}
    </ul>
  );

export const OnLeaveModal = ({ data, canOpenProfile, onClose }) => (
  <Dialog
    title="On leave today"
    subtitle={
      data?.date
        ? `${formatPlainDate(data.date, "ddd, MMM D")} · ${data.onLeave ?? 0} employee(s)`
        : undefined
    }
    onClose={onClose}
    size="lg"
  >
    <OnLeaveList
      list={data?.onLeaveList || EMPTY}
      canOpenProfile={canOpenProfile}
    />
    {(data?.onLeave ?? 0) > (data?.onLeaveList || EMPTY).length && (
      <p className="fs-075 text-slate-500 mt-2 mb-0">
        Showing the first {data.onLeaveList.length}.{" "}
        <Link to="/leave-management" onClick={onClose}>
          See all leave
        </Link>
      </p>
    )}
  </Dialog>
);

/** Who hasn't checked in today (and isn't on leave): search + paging. */
const NotCheckedInModal = ({ scope, type, canOpenProfile, onClose }) => {
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);
  const q = useNotCheckedIn({
    ...scope,
    assignmentType: type === "All" ? undefined : type,
    search: search || undefined,
    page,
    limit,
  });
  const rows = q.data?.data || EMPTY;
  return (
    <Dialog
      title="Not checked in today"
      subtitle={`${type === "All" ? "All employees" : `${type} employees`} · not on leave${q.data?.total !== undefined ? ` · ${q.data.total}` : ""}`}
      onClose={onClose}
      size="lg"
    >
      <div className="position-relative mb-2">
        <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
        <input
          type="search"
          className="filter-input w-100 ps-5"
          placeholder="Search name or employee number"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          aria-label="Search"
          autoFocus
        />
      </div>
      {q.isLoading ? (
        <div className="text-center py-4">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      ) : q.isError ? (
        <p className="fs-085 text-danger">Couldn&apos;t load the list.</p>
      ) : rows.length === 0 ? (
        <p className="fs-085 text-slate-500 my-3">
          {search ? "Nobody matches." : "Everyone has checked in."}
        </p>
      ) : (
        <ul className="dw-list">
          {rows.map((p) => {
            const name = properName(p.name);
            const row = (
              <>
                <Avatar id={p.employeeId} name={name} />
                <div className="min-w-0 flex-grow-1">
                  <div className="dw-name text-truncate">{name}</div>
                  <div className="dw-meta text-truncate">
                    {[p.employeeNumber, p.branchName, p.assignmentType]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </>
            );
            return (
              <li key={p.employeeId}>
                {canOpenProfile ? (
                  <Link
                    to={`/employee/${p.employeeId}`}
                    className="dw-row dw-link"
                    onClick={onClose}
                  >
                    {row}
                  </Link>
                ) : (
                  <div className="dw-row">{row}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Pagination
        page={page}
        limit={limit}
        total={q.data?.total || 0}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n);
          setPage(1);
        }}
        pageSizes={[20, 50, 100]}
      />
    </Dialog>
  );
};

/**
 * Attendance today (GET /dashboard/attendance-today): checked in N of M by
 * employee type, status chips that open the attendance list for today, and
 * who is on leave. `q` is the query; the card body scrolls.
 */
const AttendanceToday = ({ q, scope, canOpenProfile }) => {
  const [notInOpen, setNotInOpen] = useState(false);
  const d = q.data;
  const types = TYPES.filter((t) => t === "All" || d?.byType?.[t]);
  const [type, setType] = useState(null);
  const active = type || (d?.byType?.Internal ? "Internal" : "All");
  const c = countsFor(d, active);
  const pct = c.expected
    ? Math.round(((c.checkedIn ?? 0) / c.expected) * 100)
    : 0;
  const day = d?.date;
  const listLink = (status) =>
    `/attendance?from=${day}&to=${day}${status ? `&status=${status}` : ""}${active !== "All" ? `&assignmentType=${active}` : ""}`;
  const chips = [
    ["Present", c.present, "PRESENT", "green"],
    ["Late", c.late, "LATE", "amber"],
    ["Half day", c.halfDay, "HALF_DAY", "blue"],
    ["Not checked in", c.notCheckedIn, "ABSENT", "red"],
    ["On leave", c.onLeave, "ON_LEAVE", "purple"],
  ];

  return (
    <section className="glass-panel dash-card">
      <header className="dash-card-head">
        <h2 className="dash-card-title">
          <i className="bi bi-person-check me-2 text-primary"></i>Attendance
          today
        </h2>
        <span className="d-flex flex-wrap align-items-center gap-1">
          {day && (
            <span className="fs-075 text-slate-500">
              {formatPlainDate(day, "ddd, MMM D")}
            </span>
          )}
          {d?.holiday && (
            <span className="status-pill status-pill--purple">
              Holiday: {d.holiday.name}
            </span>
          )}
        </span>
      </header>
      <div className="dash-card-body">
        {q.isLoading ? (
          <div
            className="kpi-skeleton kpi-skeleton--block"
            aria-label="Loading"
          ></div>
        ) : q.isError ? (
          <div className="d-flex align-items-center gap-2 my-2 fs-080 text-slate-500">
            Couldn&apos;t load this right now.
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => q.refetch()}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>Retry
            </button>
          </div>
        ) : (
          <>
            {types.length > 1 && (
              <div
                className="btn-group btn-group-sm my-2"
                role="group"
                aria-label="Employee type"
              >
                {types.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`btn ${active === t ? "btn-primary" : "btn-light border"}`}
                    aria-pressed={active === t}
                    onClick={() => setType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            <div className="att-today-main">
              <span className="kpi-value">{c.checkedIn ?? 0}</span>
              <span className="text-slate-500 fs-085">
                of {c.expected ?? 0} checked in
              </span>
              <span className="ms-auto fs-080 fw-semibold text-slate-600">
                {pct}%
              </span>
            </div>
            <div
              className="progress att-today-bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${pct}% checked in`}
            >
              <div className="progress-bar" style={{ width: `${pct}%` }}></div>
            </div>
            <div className="fs-075 text-slate-500 mb-2">
              Still checked in: {c.checkedInNow ?? 0}
            </div>
            <div className="att-today-chips">
              {chips.map(([label, n, status, tone]) =>
                // No check-in usually means no attendance row: its own list
                status === "ABSENT" ? (
                  <button
                    key={status}
                    type="button"
                    className={`att-chip att-chip--${tone}`}
                    onClick={() => setNotInOpen(true)}
                  >
                    <strong>{n ?? 0}</strong> {label}
                  </button>
                ) : (
                  <Link
                    key={status}
                    to={listLink(status)}
                    className={`att-chip att-chip--${tone}`}
                  >
                    <strong>{n ?? 0}</strong> {label}
                  </Link>
                ),
              )}
            </div>
            <div className="dw-section mt-3">On leave today</div>
            <OnLeaveList
              list={(d?.onLeaveList || EMPTY).slice(0, 8)}
              canOpenProfile={canOpenProfile}
            />
          </>
        )}
      </div>
      {notInOpen && (
        <NotCheckedInModal
          scope={scope}
          type={active}
          canOpenProfile={canOpenProfile}
          onClose={() => setNotInOpen(false)}
        />
      )}
    </section>
  );
};

export default AttendanceToday;
